import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Divider,
  ListSubheader,
  type AutocompleteRenderGroupParams,
} from '@mui/material';
import {
  CommissionedInterventionModel,
  FrequencyForReportingModel,
  GrantTypeOfferCombinationModel,
  InterventionObjectiveModel,
} from '../../../../api-service/clients';
import {
  DeepPartial,
  ServiceProviderAssignmentErrors,
} from '../../common/assignment/interfaces';
import { Cell } from '../../../../components/cell';
import { Row } from '../../../../components/row';
import { MaxLengthTextField } from '../../../../components/text-field';
import { DatePicker } from '../../../../components/date-picker';
import { SingleSelect } from '../../../../components/single-select';
import { MultiSelect } from '../../../../components/multi-select';
import { InterventionObjectivesTable } from '../../common/assignment/intervention-objective/intervention-objectives-table';
import {
  formatGrantTypeOfferCombinationLabel,
  formatGrantTypeOfferCombinationSecondaryLine,
  getGrantTypeOfferCombinationGroup,
  GRANT_TYPE_OFFER_COMBO_GROUP_INVALID_VALIDITY,
  GRANT_TYPE_OFFER_COMBO_GROUP_NO_SEATS,
  isGrantTypeOfferCombinationSelectable,
  sortGrantTypeOfferCombinationsDisabledLast,
} from './grant-type-offer-combination-utils';
import { BenefitType } from '../../common/assignment/use-get-benefit-types';

interface FrequencyOption {
  value: FrequencyForReportingModel;
  label: string;
}

const allFrequencyOptions: FrequencyOption[] = [
  {
    value: FrequencyForReportingModel.NoReportingMeasurement,
    label: 'Ingen rapportering',
  },
  { value: FrequencyForReportingModel.EveryWeek, label: 'Hver uge' },
  { value: FrequencyForReportingModel.EveryOtherWeek, label: 'Hver anden uge' },
  { value: FrequencyForReportingModel.EveryMonth, label: 'Hver måned' },
  { value: FrequencyForReportingModel.Quarterly, label: 'Kvartalsvis' },
  { value: FrequencyForReportingModel.SemiAnnually, label: 'Halvårligt' },
  { value: FrequencyForReportingModel.Annually, label: 'Årligt' },
  { value: FrequencyForReportingModel.EveryOtherYear, label: 'Hvert andet år' },
  {
    value: FrequencyForReportingModel.WithXDaysInBetween,
    label: 'Med X dage imellem',
  },
  {
    value: FrequencyForReportingModel.WhenCompletingActivity,
    label: 'Ved afslutning af aktivitet',
  },
];

interface ISpCommissionedInterventionProps {
  commissionedIntervention: DeepPartial<CommissionedInterventionModel>;
  activityOfferId?: string;
  isReadOnly: boolean;
  isEditmode: boolean;
  agreementsRequired?: boolean;
  errors: Pick<
    ServiceProviderAssignmentErrors,
    | 'grantTypeId'
    | 'benefitTypeIds'
    | 'reportingFrequency'
    | 'followUpDate'
    | 'agreementsBetweenAuthorityAndProvider'
  >;
  grantTypeOfferCombinations: GrantTypeOfferCombinationModel[];
  benefitTypes: BenefitType[];
  followUpMinDate: Date;
  followUpMaxDate?: Date;
  assignmentId?: string;
  interventionObjectives: InterventionObjectiveModel[];
  interventionObjectivesIsLoading: boolean;
  onGrantTypeOfferChange: (value?: GrantTypeOfferCombinationModel) => void;
  onReportingFrequencyChange: (value?: FrequencyForReportingModel) => void;
  onDaysBetweenMeasurementsChange: (value?: number) => void;
  onFollowUpDateChange: (value?: Date) => void;
  onBenefitTypesChange: (values: BenefitType[]) => void;
  onAgreementsBetweenAuthorityAndProviderChange: (value: string) => void;
  onCreateInterventionObjective?: () => void;
  onEditInterventionObjective: (interventionObjectiveId: string) => void;
}

export function SpCommissionedIntervention(
  props: ISpCommissionedInterventionProps
) {
  const {
    commissionedIntervention,
    activityOfferId,
    isReadOnly,
    isEditmode,
    agreementsRequired = true,
    errors,
    grantTypeOfferCombinations,
    benefitTypes,
    followUpMinDate,
    followUpMaxDate,
    assignmentId,
    interventionObjectives,
    interventionObjectivesIsLoading,
    onGrantTypeOfferChange,
    onReportingFrequencyChange,
    onDaysBetweenMeasurementsChange,
    onFollowUpDateChange,
    onBenefitTypesChange,
    onAgreementsBetweenAuthorityAndProviderChange,
    onCreateInterventionObjective,
    onEditInterventionObjective,
  } = props;

  const {
    grantTypeId,
    reportingFrequency,
    daysBetweenMeasurements,
    followUpDate,
    benefitTypeIds,
    agreementsBetweenAuthorityAndProvider,
  } = commissionedIntervention || {};

  const selectedCombination = grantTypeOfferCombinations.find(
    c => c.grantTypeId === grantTypeId && c.offerId === activityOfferId
  );

  const grantTypeOfferCombinationsSorted = useMemo(
    () =>
      sortGrantTypeOfferCombinationsDisabledLast(grantTypeOfferCombinations),
    [grantTypeOfferCombinations]
  );

  const renderGrantTypeOfferGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const groupHeader = (() => {
        if (params.group === GRANT_TYPE_OFFER_COMBO_GROUP_NO_SEATS) {
          return 'Tilbud uden ledige pladser';
        }
        if (params.group === GRANT_TYPE_OFFER_COMBO_GROUP_INVALID_VALIDITY) {
          return 'Tilbud med ikke-gyldig gyldighedsperiode';
        }
        return undefined;
      })();
      return (
        <li key={params.key}>
          {groupHeader && (
            <>
              <Divider sx={{ mx: 0.5, my: 0.5 }} />
              <ListSubheader
                component="div"
                disableSticky
                sx={{
                  position: 'static',
                  lineHeight: 1.25,
                  py: 0.5,
                  px: 2,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {groupHeader}
              </ListSubheader>
            </>
          )}
          <Box
            component="ul"
            className="MuiAutocomplete-groupUl"
            sx={{ p: 0, m: 0 }}
          >
            {params.children}
          </Box>
        </li>
      );
    },
    []
  );

  const selectedFrequency = allFrequencyOptions.find(
    x => x.value === reportingFrequency
  );
  const showDaysBetweenMeasurements =
    reportingFrequency === FrequencyForReportingModel.WithXDaysInBetween;

  return (
    <Box mt={1}>
      <Row>
        <Cell size="full">
          <SingleSelect<GrantTypeOfferCombinationModel>
            label="Bevillingstype"
            getOptionLabel={formatGrantTypeOfferCombinationLabel}
            value={selectedCombination}
            options={grantTypeOfferCombinationsSorted}
            renderOption={(
              prop: object,
              option: GrantTypeOfferCombinationModel
            ) => {
              const selectable = isGrantTypeOfferCombinationSelectable(option);
              const liProps = prop as React.HTMLAttributes<HTMLLIElement>;
              return (
                <li
                  {...liProps}
                  style={{
                    ...liProps.style,
                    opacity: selectable ? 1 : 0.55,
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      py: 0.5,
                      width: '100%',
                      pr: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, lineHeight: 1.35 }}
                    >
                      {option.grantTypeName}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        pl: 2,
                        color: 'text.secondary',
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {formatGrantTypeOfferCombinationSecondaryLine(option)}
                    </Box>
                  </Box>
                </li>
              );
            }}
            getOptionDisabled={option =>
              !isGrantTypeOfferCombinationSelectable(option)
            }
            groupBy={getGrantTypeOfferCombinationGroup}
            renderGroup={renderGrantTypeOfferGroup}
            required
            onChange={onGrantTypeOfferChange}
            variant="outlined"
            error={!!errors.grantTypeId}
            errorText={errors.grantTypeId}
            disabled={isReadOnly}
          />
        </Cell>
      </Row>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <MultiSelect<BenefitType>
              label="Ydelser"
              getOptionLabel={(option: BenefitType) => option.displayName ?? ''}
              value={benefitTypes.filter(x =>
                benefitTypeIds?.some((id: string) => id === x.id)
              )}
              options={benefitTypes}
              filterOptions={(options, filterState) => {
                const filterOptions = options;
                if (filterState.inputValue) {
                  return filterOptions.filter(
                    x =>
                      x.sortField &&
                      x.sortField
                        .toLowerCase()
                        .indexOf(filterState.inputValue.toLowerCase()) >= 0
                  );
                }
                return filterOptions;
              }}
              onChange={onBenefitTypesChange}
              variant="outlined"
              disabled={isReadOnly}
              required
              error={!!errors.benefitTypeIds}
              errorText={errors.benefitTypeIds}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="half">
            <SingleSelect<FrequencyOption>
              label="Frekvens for rapportering"
              getOptionLabel={(option: FrequencyOption) => option.label}
              value={selectedFrequency}
              options={allFrequencyOptions}
              renderOption={(prop: object, option: FrequencyOption) => (
                <li {...prop} key={option.value}>
                  {option.label}
                </li>
              )}
              onChange={option => onReportingFrequencyChange(option?.value)}
              variant="outlined"
              disabled={isReadOnly}
              required
              error={!!errors.reportingFrequency}
              errorText={errors.reportingFrequency}
            />
          </Cell>
          {showDaysBetweenMeasurements && (
            <Cell size="half">
              <MaxLengthTextField
                value={daysBetweenMeasurements?.toString() || ''}
                variant="outlined"
                maxLength={4}
                label="Antal dage mellem målinger"
                fullWidth
                type="number"
                onChange={e => {
                  const { value } = e.currentTarget;
                  onDaysBetweenMeasurementsChange(
                    value ? parseInt(value, 10) : undefined
                  );
                }}
                disabled={isReadOnly}
              />
            </Cell>
          )}
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="half">
            <DatePicker
              onChange={onFollowUpDateChange}
              value={followUpDate}
              label="Opfølgningsdato"
              enableRelativeDateFormatting
              fullWidth
              variant="outlined"
              disabled={isReadOnly}
              required
              error={!!errors.followUpDate}
              errorText={errors.followUpDate}
              minDate={followUpMinDate}
              maxDate={followUpMaxDate}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <MaxLengthTextField
              value={agreementsBetweenAuthorityAndProvider || ''}
              variant="outlined"
              maxLength={10000}
              multiline
              label="Aftaler mellem myndighed og udfører om indsatsen"
              fullWidth
              rows={5}
              onChange={e =>
                onAgreementsBetweenAuthorityAndProviderChange(
                  e.currentTarget.value
                )
              }
              disabled={isReadOnly}
              required={agreementsRequired}
              error={!!errors.agreementsBetweenAuthorityAndProvider}
              errorText={errors.agreementsBetweenAuthorityAndProvider}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={2}>
        {assignmentId && (
          <InterventionObjectivesTable
            assignmentId={assignmentId}
            interventionObjectives={interventionObjectives}
            isLoading={interventionObjectivesIsLoading}
            benefitTypes={benefitTypes}
            onCreateNewClick={onCreateInterventionObjective}
            onRowClick={onEditInterventionObjective}
            isEditMode={isEditmode}
          />
        )}
      </Box>
    </Box>
  );
}
